import React, { useEffect, useMemo } from "react";
import { Card } from "react-bootstrap";
import Helmet from "react-helmet";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, NavLink, Outlet, useParams } from "react-router-dom";
import { clubListResponce } from "../../store/features/club/clubSlice";
import { clubDisplayName, findClubById } from "./tableBookingUtils";
import ShareTableSheetButton from "../../components/tableSheet/ShareTableSheetButton";
import useAuth from "../../hooks/useAuth";
import "./tableBooking.css";

const TableBookingShell = () => {
  const { t } = useTranslation();
  const { clubId } = useParams();
  const { isClubManager, managedClubId } = useAuth();
  const dispatch = useDispatch();
  const clubList = useSelector((state) => state?.club?.clubList ?? []);
  const clubsLoading = useSelector((state) => state?.club?.loading);

  useEffect(() => {
    if (!Array.isArray(clubList) || clubList.length === 0) {
      dispatch(clubListResponce());
    }
  }, [dispatch, clubList]);

  const club = useMemo(
    () => findClubById(clubList, clubId),
    [clubList, clubId]
  );

  const clubTitle = clubDisplayName(club, clubsLoading ? "…" : "—");

  if (
    isClubManager &&
    managedClubId &&
    String(clubId) !== String(managedClubId)
  ) {
    return (
      <Navigate to={`/clubs/${managedClubId}/table-booking`} replace />
    );
  }

  return (
    <React.Fragment>
      <Helmet>
        <title>{t("TABLE_BOOKING_MENU")} | RizzTix</title>
      </Helmet>
      <div className="pageContent categoryManagement table-booking-shell">
        <div className="category">
          <Card className="mainCard mainCardWrapper table-booking-shell-card mb-2">
            <Card.Body className="table-booking-shell-body">
              <div className="table-booking-shell-header">
                <div className="table-booking-shell-heading">
                  <h1 className="table-booking-shell-title mb-1">
                    {t("TABLE_BOOKING_MENU")}
                  </h1>
                  <p className="table-booking-shell-club mb-0">
                    <span className="table-booking-shell-club-label">
                      {t("TABLE_BOOKING_CLUB_ID_LABEL")}:
                    </span>{" "}
                    <strong className="table-booking-shell-club-name">
                      {clubTitle}
                    </strong>
                    {club?.slug ? (
                      <span className="table-booking-shell-club-slug">
                        {club.slug}
                      </span>
                    ) : null}
                  </p>
                </div>
                <div className="table-booking-shell-actions d-flex flex-wrap gap-2 align-items-center">
                  <ShareTableSheetButton clubId={clubId} />
                  {!isClubManager ? (
                    <NavLink
                      to="/clubs-table-booking"
                      className="btn btn-outline-secondary btn-sm table-booking-shell-change-club"
                    >
                      {t("TABLE_BOOKING_CHANGE_CLUB")}
                    </NavLink>
                  ) : null}
                </div>
              </div>
              <ul className="nav nav-pills table-booking-tabs gap-2 mt-2 mb-0">
                <li className="nav-item">
                  <NavLink
                    to={`/clubs/${clubId}/table-booking/layouts`}
                    className={({ isActive }) =>
                      `nav-link ${isActive ? "active" : ""}`
                    }
                  >
                    {t("TABLE_BOOKING_TAB_LAYOUTS")}
                  </NavLink>
                </li>
                <li className="nav-item">
                  <NavLink
                    end
                    to={`/clubs/${clubId}/table-booking/bookings`}
                    className={({ isActive }) =>
                      `nav-link ${isActive ? "active" : ""}`
                    }
                  >
                    {t("TABLE_BOOKING_TAB_BOOKINGS")}
                  </NavLink>
                </li>
              </ul>
            </Card.Body>
          </Card>
          <Outlet />
        </div>
      </div>
    </React.Fragment>
  );
};

export default TableBookingShell;
